import { OBTAINED_VALUES, UNIT_KINDS, type UnitKind } from "@sins2-atlas/game-data";
import { getRouteApi } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { z } from "zod";
import { CompareTray } from "../components/compare-tray";
import { Disclosure } from "../components/disclosure";
import { FacetDropdown, type FacetOption } from "../components/facet-dropdown";
import { CorruptionDropdown, TechDropdown } from "../components/frame-toggle";
import { RosterTable } from "../components/roster-table";
import { StatesLegend, StatesLegendRows } from "../components/states-legend";
import {
  CompareParamNotice,
  CorruptionNotice,
  FactionNotice,
  TechNotice,
  UrlNotice,
  UrlToken,
} from "../components/url-notice";
import { clampCompare, parseCompareParam, serializeCompareParam } from "../lib/compare";
import { dataset } from "../lib/dataset";
import { DERIVED_COLUMN_DISCLOSURES } from "../lib/disclosures";
import {
  ALL_FACTIONS,
  factionLinkSearch,
  isAllFactions,
  matchesFactionSelection,
  parseFactionSelection,
  serializeFactionSelection,
} from "../lib/faction-selection";
import { titleCase } from "../lib/format";
import { parseRosterFrame, type StatFrame, serializeFrame } from "../lib/frame";
import { ROSTER_STATS_NOTE } from "../lib/frame-labels";
import {
  CLASS_TOKEN_BY_KIND,
  CLASS_TOKENS,
  type ClassToken,
  hasActiveFilters,
  hasUnknownTokens,
  matchesFilters,
  NO_FILTERS,
  OBTAINED_TOKEN_BY_VALUE,
  OBTAINED_TOKENS,
  type ObtainedToken,
  parseRosterFilters,
  parseUnknownFacetTokens,
  type RosterFilters,
  SLOT_TOKENS,
  type SlotToken,
  serializeRosterFilters,
  toggleToken,
  type UnknownFacetTokens,
} from "../lib/roster-filters";
import { statModelFor, type ViewKind } from "../lib/stat-model";
import { techPickerRaces } from "../lib/tech-index";
import { useDocumentTitle } from "../lib/title";
import { type RosterView, SHIPS_VIEW, STRUCTURES_VIEW } from "../lib/views";
import { OBTAINED_META, UNIT_KIND_LABELS } from "../lib/vocabulary";

// The Ships and Structures URL contract (ADR 0002: everything in the URL — the schema is a
// public API; ADR 0008: two views over one Roster). Both routes share it, and /roster — the
// address before the split — redirects to /ships with its whole query string (router.tsx).
// `sort` is `<column>.<direction>`, e.g. ?sort=hull.desc; absent means the default
// (name.asc), and a view accepts only the columns its own table has. The facet params
// (on Ships class and obtained; on Structures slot) are comma-separated token lists (see
// lib/roster-filters.ts); absent means unfiltered. `faction` is the site-wide Faction
// Selection (lib/faction-selection.ts, ADR 0010), absent means All Factions. `tech` and
// `corruption` are the stat frame pair (lib/frame.ts, ADR 0007 + ADR 0011): ?tech= is the
// whole research axis (absent Base, `all` Fully-Teched, else a Tech Set's maximal elements);
// ?corruption=mild|moderate|extreme, absent means uncorrupted. `compare` is the pending
// comparison set (lib/compare.ts): ordered unit ids, absent means empty. Invalid values never
// error a shared link, but a facet token nothing recognizes is reported on the page rather
// than dropped (ADR 0002 amendment 2026-07-30) — the view always matches the URL, or says why
// it cannot. Additions are backwards-compatible; existing keys never change meaning.
export const SORT_COLUMNS = [
  "name",
  "race",
  "class",
  "cloaking",
  "slotCost",
  "hull",
  "armor",
  "shields",
  "durability",
  "armorStrength",
  "corruption",
  "ehp",
  "dps",
  "novaDamage",
  "dpsPerSupply",
  "dpsPerCost",
  "costPerHp",
  "speed",
  "range",
  "bombingRate",
  "populationRate",
  "credits",
  "metal",
  "crystal",
  "exotics",
  "supply",
  "buildTime",
  "cloakDetection",
] as const;

export type SortColumn = (typeof SORT_COLUMNS)[number];
type SortDir = "asc" | "desc";
type SortKey = `${SortColumn}.${SortDir}`;

/** The table's own columns, sortable on every view: the identity columns and the exotics
 * addition to Cost. Everything else a view can sort is a row the model gives its kind. */
const TABLE_SORT_COLUMNS: readonly SortColumn[] = ["name", "race", "class", "exotics"];

/** The columns a view can sort — derived from the model, so a row a kind has is sortable
 * there by construction and a row it lacks (Supply on Structures, Orbit Slot Cost on Ships)
 * is not offered a sort the table could not perform. SORT_COLUMNS is the union vocabulary. */
export function sortColumnsFor(kind: ViewKind): readonly SortColumn[] {
  const modelIds = new Set(
    statModelFor([kind]).flatMap((group) => group.rows.map((row) => row.id)),
  );
  return SORT_COLUMNS.filter(
    (column) => TABLE_SORT_COLUMNS.includes(column) || modelIds.has(column),
  );
}

function sortKeySchema(kind: ViewKind) {
  const keys = new Set<string>(sortColumnsFor(kind).flatMap((c) => [`${c}.asc`, `${c}.desc`]));
  return z
    .custom<SortKey>((value) => typeof value === "string" && keys.has(value))
    .optional()
    .catch(undefined);
}

const DEFAULT_SORT: SortKey = "name.asc";

const facetParam = z.string().optional().catch(undefined);

// The keys both views carry besides their facets: the stat frame's two keys and the
// pending compare set. `tech` is the whole research axis (lib/tech-set.ts, ADR 0009 +
// ADR 0011): absent is Base, `all` is Fully-Teched, and a comma-separated list is that Tech
// Set's maximal elements. `?frame=` retired with the second research key.
const sharedSearch = {
  // `faction` is the site-wide Faction Selection (lib/faction-selection.ts, ADR 0010) —
  // the header's key, not a per-view facet, which is why it sits beside the stat frame's
  // keys rather than with Class and Slot type. `race` is retired: a Race tick serializes
  // as its Factions.
  faction: facetParam,
  corruption: facetParam,
  tech: facetParam,
  compare: facetParam,
};

export const shipsSearchSchema = z.object({
  sort: sortKeySchema("ships"),
  class: facetParam,
  obtained: facetParam,
  ...sharedSearch,
});

// No Class or Obtained: neither can change the Structures list. Slot type instead (Issue
// 29): `slot` is civilian | military, the game's two structure build groups.
export const structuresSearchSchema = z.object({
  sort: sortKeySchema("structures"),
  slot: facetParam,
  ...sharedSearch,
});

/** The superset search both views read: every key either view has. A view's own schema
 * never admits the other's facets, so those keys are always undefined there and stay out
 * of its URL. */
type RosterSearch = z.infer<typeof shipsSearchSchema> & z.infer<typeof structuresSearchSchema>;

function parseSortKey(key: SortKey): { column: SortColumn; dir: SortDir } {
  const [column, dir] = key.split(".") as [SortColumn, SortDir];
  return { column, dir };
}

// The class options follow the kind ladder; obtained follows its facet order.
const CLASS_OPTIONS: FacetOption<ClassToken>[] = UNIT_KINDS.map((kind: UnitKind) => ({
  token: CLASS_TOKEN_BY_KIND[kind],
  label: UNIT_KIND_LABELS[kind],
}));
const OBTAINED_OPTIONS: FacetOption<ObtainedToken>[] = OBTAINED_VALUES.map((value) => ({
  token: OBTAINED_TOKEN_BY_VALUE[value],
  label: OBTAINED_META[value].label,
  title: OBTAINED_META[value].title,
}));
// The Slot type options are the game's two structure build-group names, read off the
// dataset (structure_build_group_name_civilian / _military ship as "CIVILIAN" / "MILITARY")
// in the site's Title Case — the Class labels' convention. Exported for the route test.
export const SLOT_OPTIONS: FacetOption<SlotToken>[] = SLOT_TOKENS.map((token) => ({
  token,
  label: titleCase(dataset.orbitSlotLabels[token].name),
}));

// The unknown-token notice DOES use a descriptor table where the dropdowns below deliberately
// do not: it only ever reads `unknown[key]`, which is a plain string[] for all five facets, so
// none of the per-facet token typing that made a unified dropdown table need casts applies.
const FACET_VOCABULARY: ReadonlyArray<{
  key: keyof UnknownFacetTokens;
  noun: string;
  options: ReadonlyArray<{ label: string }>;
}> = [
  { key: "class", noun: "Class", options: CLASS_OPTIONS },
  { key: "obtained", noun: "Obtained filter", options: OBTAINED_OPTIONS },
  { key: "slot", noun: "Slot type", options: SLOT_OPTIONS },
];

/** Say which tokens in the URL name nothing, and what the real vocabulary is (ADR 0002
 * amendment): a shared link is never silently reinterpreted into a different view. Only the
 * view's own facets can carry a token — the Structures schema drops the other two. */
function UnknownTokenNotice({ view, unknown }: { view: RosterView; unknown: UnknownFacetTokens }) {
  return (
    <>
      {FACET_VOCABULARY.filter(
        (facet) => view.facets.includes(facet.key) && unknown[facet.key].length > 0,
      ).map((facet) => {
        const tokens = unknown[facet.key];
        return (
          <UrlNotice key={facet.key}>
            Unrecognized {facet.noun}
            {tokens.length > 1 ? "s" : ""}: <UrlToken>{tokens.join(", ")}</UrlToken> — no unit
            matches. The {facet.noun} values are{" "}
            {facet.options.map((option) => option.label).join(", ")}.
          </UrlNotice>
        );
      })}
    </>
  );
}

// The three dropdowns stay written out rather than driven from a descriptor table: each
// facet carries its own token type, and every generic table that unified them needed either
// a cast on `filters[key]` or a five-parameter helper — more machinery than the repetition
// costs. Each toggle names the canonical token list that already exists (CLASS_TOKENS /
// OBTAINED_TOKENS / SLOT_TOKENS). A view renders the dropdowns its `facets` list names, in
// that order. Race and Faction are NOT among them any more: one Faction Picker in the
// header holds the site-wide Faction Selection (ADR 0010), so there are no two controls
// that can disagree about which empire the reader is playing.

/** Why this view is empty, in the reader's terms: the header's Faction Selection and the
 * view's own facets can each empty it, and an empty page that does not say which reads as a
 * broken one (ADR 0010). `inSelection` is the count the selection alone leaves. */
function emptyReason(
  view: RosterView,
  selection: ReturnType<typeof parseFactionSelection>,
  inSelection: number,
): string {
  if (isAllFactions(selection)) return `No ${view.noun} match the current filters.`;
  if (inSelection === 0) return `No ${view.noun} belong to the selected Factions.`;
  return `No ${view.noun} in the selected Factions match the current filters.`;
}

/** One view over the Roster: the table, its facets and the explainer, for the units and the
 * model rows of `view.kind`. The two routes below differ only in the view they hand in and
 * the route whose search they read and write. */
function RosterViewPage({
  view,
  search,
  onSearchChange,
}: {
  view: RosterView;
  search: RosterSearch;
  onSearchChange: (patch: Partial<RosterSearch>) => void;
}) {
  useDocumentTitle(view.heading);
  const { units } = view;

  const { column, dir } = parseSortKey(search.sort ?? DEFAULT_SORT);
  const sorting: SortingState = [{ id: column, desc: dir === "desc" }];
  const filters = parseRosterFilters(search);
  const unknown = parseUnknownFacetTokens(search);
  const frame = parseRosterFrame(search);
  const requestedCompare = parseCompareParam(search.compare);
  const compare = clampCompare(requestedCompare);
  // The site-wide Faction Selection narrows the view before its own facets do (ADR 0010),
  // so the page can tell the reader WHICH of the two emptied it rather than showing a bare
  // empty table under a header control they may not have noticed.
  const selection = parseFactionSelection(search);
  const inSelection = units.filter((unit) => matchesFactionSelection(unit, selection));
  const filtered = inSelection.filter((unit) => matchesFilters(unit, filters, unknown));
  // The tick list offers the Races the table is actually showing, narrowed by the header's
  // Faction Selection (ADR 0010): filtering to one Race should not offer three others' techs,
  // which could move nothing on screen, and a facet that empties the table should not empty
  // the reader's own Race out of the Tech control either (lib/tech-index: techPickerRaces).
  // The control itself then widens this by any Race the Tech Set already holds tech for, so
  // narrowing the selection can never hide a member it still counts (ADR 0010 amendment).
  const racesOnPage = techPickerRaces(selection, new Set(filtered.map((unit) => unit.race)));
  // The explainer lists the formulas behind this view's own columns, no others.
  const rowIds = new Set(statModelFor([view.kind]).flatMap((g) => g.rows.map((row) => row.id)));
  const disclosures = DERIVED_COLUMN_DISCLOSURES.filter((d) => rowIds.has(d.id));

  function onSortingChange(next: SortingState) {
    const entry = next[0];
    const key = entry ? (`${entry.id}.${entry.desc ? "desc" : "asc"}` as SortKey) : undefined;
    // The default sort stays out of the URL so the bare /ships or /structures link is canonical.
    onSearchChange({ sort: key === DEFAULT_SORT ? undefined : key });
  }

  function onFrameChange(next: StatFrame) {
    onSearchChange(serializeFrame(next));
  }

  function onFiltersChange(next: RosterFilters) {
    onSearchChange(serializeRosterFilters(next));
  }

  function onCompareChange(next: string[]) {
    onSearchChange({ compare: serializeCompareParam(next) });
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-y-2">
        <h1 className="text-[22px] font-semibold tracking-tight">{view.heading}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <TechDropdown frame={frame} races={racesOnPage} onChange={onFrameChange} />
          <CorruptionDropdown frame={frame} onChange={onFrameChange} />
          {/* The count is filter feedback only — the unfiltered header stays decluttered
              (user decision, 479a5b3) — and it no longer names the frame: the two controls
              beside it already say what is applied, in the same words (user decision
              2026-09-06). The disclosure below still spells the frame out in full. */}
          {filtered.length !== units.length && (
            <span className="font-mono text-[12.5px] text-muted-2">
              {filtered.length} of {units.length} {view.noun}
            </span>
          )}
          <StatesLegend />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {view.facets.includes("class") && (
          <FacetDropdown
            label="Class"
            options={CLASS_OPTIONS}
            selected={filters.class}
            onToggle={(token) =>
              onFiltersChange({
                ...filters,
                class: toggleToken(filters.class, token, CLASS_TOKENS),
              })
            }
          />
        )}
        {view.facets.includes("obtained") && (
          <FacetDropdown
            label="Obtained"
            options={OBTAINED_OPTIONS}
            selected={filters.obtained}
            onToggle={(token) =>
              onFiltersChange({
                ...filters,
                obtained: toggleToken(filters.obtained, token, OBTAINED_TOKENS),
              })
            }
          />
        )}
        {view.facets.includes("slot") && (
          <FacetDropdown
            label="Slot type"
            options={SLOT_OPTIONS}
            selected={filters.slot}
            onToggle={(token) =>
              onFiltersChange({ ...filters, slot: toggleToken(filters.slot, token, SLOT_TOKENS) })
            }
          />
        )}
        {/* Unknown tokens set no chip, so Clear must key off them too — otherwise a link
            like ?faction=rebel lists nothing with no visible way back. */}
        {(hasActiveFilters(filters) || hasUnknownTokens(unknown)) && (
          <button
            type="button"
            className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.08em] text-muted-2 underline decoration-dotted underline-offset-2 hover:text-ink-3"
            onClick={() => onFiltersChange(NO_FILTERS)}
          >
            Clear
          </button>
        )}
      </div>
      {hasUnknownTokens(unknown) && <UnknownTokenNotice view={view} unknown={unknown} />}
      <FactionNotice selection={selection} />
      <CorruptionNotice value={search.corruption} />
      <TechNotice search={search} />
      <CompareParamNotice value={search.compare} requested={requestedCompare} reportOverflow />
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-line bg-surface px-4 py-10 text-center text-[13px] text-ink-3">
          <span>{emptyReason(view, selection, inSelection.length)}</span>
          <span className="flex flex-wrap items-center justify-center gap-2">
            {(hasActiveFilters(filters) || hasUnknownTokens(unknown)) && (
              <button
                type="button"
                className="cursor-pointer rounded border border-line px-2 py-1 font-mono text-[11.5px] uppercase tracking-[0.06em] text-ink-2 hover:border-line-strong"
                onClick={() => onFiltersChange(NO_FILTERS)}
              >
                Clear filters
              </button>
            )}
            {!isAllFactions(selection) && (
              <button
                type="button"
                className="cursor-pointer rounded border border-line px-2 py-1 font-mono text-[11.5px] uppercase tracking-[0.06em] text-ink-2 hover:border-line-strong"
                onClick={() => onSearchChange(serializeFactionSelection(ALL_FACTIONS))}
              >
                All Factions
              </button>
            )}
          </span>
        </div>
      ) : (
        <RosterTable
          units={filtered}
          kind={view.kind}
          sorting={sorting}
          onSortingChange={onSortingChange}
          frame={frame}
          faction={factionLinkSearch(search)}
          compare={compare}
          onCompareChange={onCompareChange}
        />
      )}
      <Disclosure summary="How are these stats computed?">
        <p className="mt-3 text-ink-3">{ROSTER_STATS_NOTE}</p>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-[auto_1fr]">
          {disclosures.map((d) => (
            <div key={d.id} className="contents">
              <dt className="whitespace-nowrap font-mono text-[12px] uppercase tracking-[0.08em] text-ink-2">
                {d.header}
                {d.sub ? <span className="ml-1 normal-case text-muted">({d.sub})</span> : null}
              </dt>
              <dd>
                <span className="font-mono text-[13.5px] text-ink-2">{d.formula}</span>
                {d.caption && <span className="ml-2 text-muted-2">{d.caption}</span>}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-muted-2">
          Every number is read from the game files. How to read the cells:
        </p>
        <StatesLegendRows className="mt-3" />
      </Disclosure>
      <CompareTray
        compare={compare}
        frame={frame}
        faction={factionLinkSearch(search)}
        onChange={onCompareChange}
      />
    </section>
  );
}

const shipsRoute = getRouteApi("/ships");

export function ShipsPage() {
  const search = shipsRoute.useSearch();
  const navigate = shipsRoute.useNavigate();
  return (
    <RosterViewPage
      view={SHIPS_VIEW}
      search={search}
      onSearchChange={(patch) => void navigate({ search: (prev) => ({ ...prev, ...patch }) })}
    />
  );
}

const structuresRoute = getRouteApi("/structures");

export function StructuresPage() {
  const search = structuresRoute.useSearch();
  const navigate = structuresRoute.useNavigate();
  return (
    <RosterViewPage
      view={STRUCTURES_VIEW}
      search={search}
      // The shared filter serializer writes every facet key; the two this route has no
      // facet for are always undefined here (the schema never admits them), and an
      // undefined key stays out of the URL.
      onSearchChange={(patch) => void navigate({ search: (prev) => ({ ...prev, ...patch }) })}
    />
  );
}
